import { useCallback, useEffect, useRef } from 'react'
import { BookOpenText, ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  closestCenter,
  type CollisionDetection,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { NotebookNote } from '../../../shared/types/notebook'
import { useMultiExpandable } from '../../../shared/hooks/useExpandable'
import { getRootNotes, getChildren, getPinnedNotes, hasChildren as checkHasChildren } from '../notebookTree'
import { findNote } from '../notebookTree'
import { useTreeDnd } from '../useTreeDnd'
import SortablePageRow from './SortablePageRow'
import DragOverlayRow from './DragOverlayRow'
import { expandCollapse } from '@tendbot/ui'

interface NotebookPageListProps {
  notes: NotebookNote[]
  activePageId?: string | null
  autoExpandIds?: string[]
  onNavigate: (id: string) => void
  onCreateChild: (parentId: string | null) => void
  onMoveNote: (noteId: string, newParentId: string | null, newSortOrder: number) => void
}

export default function NotebookPageList({ notes, activePageId, autoExpandIds, onNavigate, onCreateChild, onMoveNote }: NotebookPageListProps) {
  const { t } = useTranslation('notebook')
  const tree = useMultiExpandable([])

  // Auto-expand parent nodes when new child pages are created
  const expandedAutoIds = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!autoExpandIds) return
    for (const id of autoExpandIds) {
      if (!expandedAutoIds.current.has(id)) {
        expandedAutoIds.current.add(id)
        tree.expand(id)
      }
    }
  }, [autoExpandIds, tree])
  const sections = useMultiExpandable(['pinned', 'pages'])

  const pinnedNotes = getPinnedNotes(notes)
  const rootNotes = getRootNotes(notes)

  const {
    sensors,
    dndState,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  } = useTreeDnd({
    notes,
    onMoveNote,
    expandedIds: tree.expandedIds,
    onExpand: tree.expand,
  })

  // Use pointerWithin when pointer is inside a droppable (for before/inside/after zones),
  // fall back to closestCenter when pointer is outside all droppables (above first / below last item)
  const collisionDetection: CollisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args)
    if (pointerCollisions.length > 0) return pointerCollisions
    return closestCenter(args)
  }, [])

  const activeNote = dndState.activeDragId ? findNote(notes, dndState.activeDragId) : null

  // Build flat ID lists for SortableContext
  function collectIds(parentNotes: NotebookNote[]): string[] {
    const ids: string[] = []
    for (const note of parentNotes) {
      ids.push(note.id)
      if (tree.isExpanded(note.id)) {
        ids.push(...collectIds(getChildren(notes, note.id)))
      }
    }
    return ids
  }

  const pinnedIds = collectIds(pinnedNotes)
  const pagesIds = collectIds(rootNotes)

  function renderTree(parentNotes: NotebookNote[], depth: number) {
    return parentNotes.map(note => {
      const noteHasChildren = checkHasChildren(notes, note.id)
      const expanded = tree.isExpanded(note.id)
      const children = expanded ? getChildren(notes, note.id) : []

      return (
        <div key={note.id}>
          <SortablePageRow
            note={note}
            depth={depth}
            isExpanded={expanded}
            isActive={note.id === activePageId}
            hasChildren={noteHasChildren}
            activeDragId={dndState.activeDragId}
            dropTargetId={dndState.dropTargetId}
            dropPosition={dndState.dropPosition}
            onToggleExpand={() => tree.toggle(note.id)}
            onNavigate={onNavigate}
            onCreateChild={onCreateChild}
          />
          <AnimatePresence>
            {expanded && children.length > 0 && (
              <motion.div
                {...expandCollapse}
                transition={{ duration: 0.15 }}
                className="overflow-hidden"
              >
                {renderTree(children, depth + 1)}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )
    })
  }

  if (notes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 rounded-full bg-charcoal/50 flex items-center justify-center mb-4">
          <BookOpenText size={24} className="text-pearl/60" />
        </div>
        <p className="text-pearl/50 text-sm">{t('noPagesYet')}</p>
      </div>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="px-4 pb-24">
        {/* Pinned Section */}
        {pinnedNotes.length > 0 && (
          <div className="mb-2">
            <button
              onClick={() => sections.toggle('pinned')}
              className="flex items-center gap-2 px-2 py-2.5 w-full text-left"
            >
              <motion.div
                animate={{ rotate: sections.isExpanded('pinned') ? 0 : -90 }}
                transition={{ duration: 0.15 }}
              >
                <ChevronDown size={12} className="text-pearl/55" />
              </motion.div>
              <span className="text-xs text-pearl/50 uppercase tracking-wider font-medium">{t('sections.pinned')}</span>
            </button>
            <AnimatePresence>
              {sections.isExpanded('pinned') && (
                <motion.div
                  {...expandCollapse}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden"
                >
                  <SortableContext items={pinnedIds} strategy={verticalListSortingStrategy}>
                    {renderTree(pinnedNotes, 0)}
                  </SortableContext>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Pages Section */}
        <div>
          <button
            onClick={() => sections.toggle('pages')}
            className="flex items-center gap-2 px-2 py-2.5 w-full text-left"
          >
            <motion.div
              animate={{ rotate: sections.isExpanded('pages') ? 0 : -90 }}
              transition={{ duration: 0.15 }}
            >
              <ChevronDown size={12} className="text-pearl/55" />
            </motion.div>
            <span className="text-xs text-pearl/60 uppercase tracking-wider font-medium">{t('sections.pages')}</span>
          </button>
          <AnimatePresence>
            {sections.isExpanded('pages') && (
              <motion.div
                {...expandCollapse}
                transition={{ duration: 0.15 }}
                className="overflow-hidden"
              >
                <SortableContext items={pagesIds} strategy={verticalListSortingStrategy}>
                  {renderTree(rootNotes, 0)}
                </SortableContext>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Drag overlay — renders in a portal above everything */}
      <DragOverlay dropAnimation={null}>
        {activeNote ? <DragOverlayRow note={activeNote} /> : null}
      </DragOverlay>
    </DndContext>
  )
}
